import {
  LOGICAL_HEIGHT,
  LOGICAL_WIDTH,
  type InputAction,
} from "./types";

const keyMap = new Map<string, InputAction>([
  ["ArrowLeft", "left"],
  ["KeyA", "left"],
  ["ArrowRight", "right"],
  ["KeyD", "right"],
  ["ArrowUp", "up"],
  ["KeyW", "up"],
  ["ArrowDown", "down"],
  ["KeyS", "down"],
  ["Space", "primary"],
  ["Enter", "primary"],
  ["ShiftLeft", "secondary"],
  ["ShiftRight", "secondary"],
]);

export interface PointerState {
  x: number;
  y: number;
  active: boolean;
}

export class InputController {
  readonly pointer: PointerState = {
    x: LOGICAL_WIDTH / 2,
    y: LOGICAL_HEIGHT / 2,
    active: false,
  };

  private readonly held = new Set<InputAction>();
  private readonly pressed = new Set<InputAction>();
  private enabled = false;
  private readonly virtualBindings = new Map<
    HTMLButtonElement,
    { start: (event: PointerEvent) => void; end: (event: PointerEvent) => void }
  >();

  constructor(private readonly canvas: HTMLCanvasElement) {
    window.addEventListener("keydown", this.handleKeyDown, { passive: false });
    window.addEventListener("keyup", this.handleKeyUp);
    window.addEventListener("blur", this.clear);
    canvas.addEventListener("pointermove", this.handlePointerMove);
    canvas.addEventListener("pointerdown", this.handlePointerDown);
    canvas.addEventListener("pointerup", this.handlePointerUp);
    canvas.addEventListener("pointercancel", this.handlePointerUp);
  }

  isDown(action: InputAction): boolean {
    return this.held.has(action);
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) this.clear();
  }

  consumePressed(action: InputAction): boolean {
    const wasPressed = this.pressed.has(action);
    this.pressed.delete(action);
    return wasPressed;
  }

  endFrame(): void {
    this.pressed.clear();
  }

  bindVirtualButton(button: HTMLButtonElement, action: InputAction): void {
    const start = (event: PointerEvent): void => {
      event.preventDefault();
      button.setPointerCapture(event.pointerId);
      this.press(action);
    };
    const end = (event: PointerEvent): void => {
      event.preventDefault();
      this.release(action);
    };
    button.addEventListener("pointerdown", start);
    button.addEventListener("pointerup", end);
    button.addEventListener("pointercancel", end);
    button.addEventListener("lostpointercapture", end);
    this.virtualBindings.set(button, { start, end });
  }

  destroy(): void {
    window.removeEventListener("keydown", this.handleKeyDown);
    window.removeEventListener("keyup", this.handleKeyUp);
    window.removeEventListener("blur", this.clear);
    this.canvas.removeEventListener("pointermove", this.handlePointerMove);
    this.canvas.removeEventListener("pointerdown", this.handlePointerDown);
    this.canvas.removeEventListener("pointerup", this.handlePointerUp);
    this.canvas.removeEventListener("pointercancel", this.handlePointerUp);
    for (const [button, binding] of this.virtualBindings) {
      button.removeEventListener("pointerdown", binding.start);
      button.removeEventListener("pointerup", binding.end);
      button.removeEventListener("pointercancel", binding.end);
      button.removeEventListener("lostpointercapture", binding.end);
    }
    this.virtualBindings.clear();
    this.clear();
  }

  private readonly press = (action: InputAction): void => {
    if (!this.enabled) return;
    if (!this.held.has(action)) this.pressed.add(action);
    this.held.add(action);
  };

  private readonly release = (action: InputAction): void => {
    this.held.delete(action);
  };

  private readonly handleKeyDown = (event: KeyboardEvent): void => {
    if (!this.enabled) return;
    const action = keyMap.get(event.code);
    if (!action) return;
    if (event.target !== this.canvas) return;
    event.preventDefault();
    if (action === "left" || action === "right" || action === "up" || action === "down") {
      this.pointer.active = false;
    }
    if (!event.repeat) this.press(action);
  };

  private readonly handleKeyUp = (event: KeyboardEvent): void => {
    if (!this.enabled) return;
    const action = keyMap.get(event.code);
    if (!action) return;
    if (event.target !== this.canvas) {
      this.release(action);
      return;
    }
    event.preventDefault();
    this.release(action);
  };

  private readonly updatePointer = (event: PointerEvent): void => {
    const bounds = this.canvas.getBoundingClientRect();
    if (bounds.width <= 0 || bounds.height <= 0) return;
    const scale = Math.min(bounds.width / LOGICAL_WIDTH, bounds.height / LOGICAL_HEIGHT);
    const contentWidth = LOGICAL_WIDTH * scale;
    const contentHeight = LOGICAL_HEIGHT * scale;
    const offsetX = (bounds.width - contentWidth) / 2;
    const offsetY = (bounds.height - contentHeight) / 2;
    this.pointer.x = Math.max(
      0,
      Math.min(
        LOGICAL_WIDTH,
        ((event.clientX - bounds.left - offsetX) / contentWidth) * LOGICAL_WIDTH,
      ),
    );
    this.pointer.y = Math.max(
      0,
      Math.min(
        LOGICAL_HEIGHT,
        ((event.clientY - bounds.top - offsetY) / contentHeight) * LOGICAL_HEIGHT,
      ),
    );
  };

  private readonly handlePointerMove = (event: PointerEvent): void => {
    if (!this.enabled) return;
    this.updatePointer(event);
    this.pointer.active = true;
  };

  private readonly handlePointerDown = (event: PointerEvent): void => {
    if (!this.enabled) return;
    event.preventDefault();
    this.updatePointer(event);
    this.pointer.active = true;
    this.canvas.focus();
    this.canvas.setPointerCapture(event.pointerId);
    this.press("primary");
  };

  private readonly handlePointerUp = (event: PointerEvent): void => {
    if (!this.enabled) return;
    event.preventDefault();
    this.release("primary");
  };

  private readonly clear = (): void => {
    this.held.clear();
    this.pressed.clear();
    this.pointer.active = false;
  };
}
